import { useStyles } from './styles';
import Layout from 'components/Layout';
import { BasicTable, CircularIndeterminate, DateFormattedUtils, PaginationControlled, SelectComponent, TextInput, Title, useDebounce } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import { Button, Fade, FormControlLabel, Grid2, IconButton, InputAdornment, Switch, Typography } from '@mui/material';
import { Check, Edit, Search, Sync } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useCurrencies } from 'core/contexts/currency';
import { Field, Form } from 'react-final-form';
import { toast } from 'react-toastify';

const CurrencyRateForm = ({ currency, currencyToEdit, setCurrencyToEdit, onSubmit }) => {
    const [isEditable, setIsEditable] = useState(false);

    useEffect(() => {
        if (currencyToEdit?.id === currency?.id) {
            setIsEditable(true);
        } else {
            setIsEditable(false);
        }
    }, [currencyToEdit, currency?.id]);

    return (
        <Form
            key={currency?.id || currencyToEdit?.id || 'new'}
            onSubmit={onSubmit}
            initialValues={currencyToEdit}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <Grid2 container spacing={1} alignItems="stretch">
                        <Grid2
                            width="72px"
                            onClick={() => {
                                if (!currency?.isOverride || currency?.code === 'USD') {
                                    return;
                                }

                                if (!isEditable || !currencyToEdit?.id) {
                                    setCurrencyToEdit(currency);
                                    setIsEditable(true);
                                }
                            }}>
                            <Field id="rate" name="rate" component={TextInput} isEditable={isEditable} displayValue={currency?.rate} />
                        </Grid2>

                        {isEditable && (
                            <Grid2>
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    sx={{
                                        height: '51.5px', // Misma altura que el input :)
                                        minWidth: '36px',
                                    }}
                                    onClick={(e) => {
                                        handleSubmit(e);
                                        setIsEditable(false);
                                        setCurrencyToEdit(null);
                                    }}>
                                    <Check />
                                </Button>
                            </Grid2>
                        )}
                    </Grid2>
                </form>
            )}
        />
    );
};

const CurrenciesPage = () => {
    const classes = useStyles();
    const { t: translate, i18n } = useTranslation();
    const i18nLang = i18n.language;
    const { currencies, total, loading, getCurrencies, updateCurrency } = useCurrencies();
    const [code, setCode] = useState('');
    const [overrideFilter, setOverrideFilter] = useState('all');
    const [currencyToEdit, setCurrencyToEdit] = useState(null);
    const [page, setPage] = useState(1);
    const codeDebounce = useDebounce(code, 300);
    const MAX_PAGE = 30;

    const overrideOptions = [
        { id: 'all', name: translate('currency.override_options.all'), value: 'all' },
        { id: 'false', name: translate('currency.override_options.false'), value: 'false' },
        { id: 'true', name: translate('currency.override_options.true'), value: 'true' },
    ];

    const columns = [
        { align: 'left', val: 'id', label: 'ID' },
        { align: 'left', val: 'code', label: translate('currency.columns.code'), style: { minWidth: '84px' } },
        { align: 'left', val: 'rate', label: translate('currency.columns.rate'), style: { minWidth: '160px' } },
        { align: 'left', val: 'lastUpdated', label: translate('currency.columns.last_update'), style: { minWidth: '160px' } },
        { align: 'left', val: 'isOverride', label: translate('currency.columns.is_override'), style: { width: '160px' } },
        { val: 'actions', label: ' ', style: { width: '100px' } },
    ];

    const renderLastUpdate = ({ lastUpdated, isOverride, code }) => {
        let date;

        if (code === 'USD') {
            return <Typography fontSize="1rem">{translate('currency.non_update_currency')}</Typography>;
        }

        if (lastUpdated) {
            date = DateFormattedUtils(lastUpdated, i18nLang, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            });
        }
        return (
            <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                <Typography fontSize="1rem">{date ? date : translate('currency.render_last_update.new')}</Typography>
                {!date ? (
                    <Typography fontSize=".8rem" color="#e4954b">
                        {translate('currency.render_last_update.first_sync')}
                    </Typography>
                ) : !isOverride ? (
                    <Typography fontSize=".8rem" color="#50c878">
                        {translate('currency.render_last_update.auto_update')}
                    </Typography>
                ) : (
                    ''
                )}
            </Grid2>
        );
    };

    const prettifyCurrencies = (currencies = []) =>
        currencies.map((currency) => ({
            ...currency,
            code: (
                <Grid2 container flexDirection="column" spacing={1} justifyContent="center" alignItems="flex-start">
                    <Typography
                        sx={{
                            color: currency?.code === 'USD' ? '#50c878' : '#137dc5',
                            fontWeight: 'bold',
                            backgroundColor: currency?.code === 'USD' ? 'rgba(80, 200, 120, 0.1)' : 'rgba(19, 125, 197, 0.1)',
                            borderRadius: '8px',
                            padding: '8px',
                            minWidth: '32px',
                            textAlign: 'center',
                            display: 'inline-block',
                        }}>
                        {currency?.code}
                    </Typography>
                    {currency?.code === 'USD' && <Typography fontSize=".8rem">{translate('currency.base_currency')}</Typography>}
                </Grid2>
            ),

            rate: <CurrencyRateForm currency={currency} currencyToEdit={currencyToEdit} setCurrencyToEdit={setCurrencyToEdit} onSubmit={onSubmitUpdate} />,
            lastUpdated: renderLastUpdate(currency),
            isOverride: (
                <FormControlLabel
                    disabled={currency?.code === 'USD'}
                    control={<Switch checked={currency?.isOverride} />}
                    onChange={(e) => {
                        const value = e?.target?.checked;
                        onSubmitUpdate({ ...currency, isOverride: value });
                    }}
                    label={currency?.isOverride ? 'Manual' : 'Auto'}
                />
            ),
            actions: (
                <Grid2 container justifyContent="center" alignItems="center" spacing={1} maxWidth="fit-content">
                    <IconButton
                        className={classes.tableButton}
                        disabled={!currency?.isOverride || currency?.code === 'USD'}
                        onClick={() => {
                            setCurrencyToEdit(currency);
                        }}>
                        <Edit />
                    </IconButton>
                    <IconButton
                        className={classes.tableButton}
                        disabled={currency?.isOverride || currency?.code === 'USD'}
                        onClick={() => {
                            if (currency?.isOverride) {
                                setCurrencyToEdit(null);
                                return;
                            } else {
                                onSubmitUpdate({ ...currency, forceUpdate: true });
                            }
                        }}>
                        <Sync />
                    </IconButton>
                </Grid2>
            ),
        }));

    const handlePageChange = (_event, value) => {
        setPage(value);
    };

    const onChangeCode = (event) => {
        setCode(event.target.value);
        if (page > 1) {
            setPage(1);
        }
    };

    const onChangeFilter = (option) => {
        setOverrideFilter(option?.value ?? 'all');
        if (page > 1) {
            setPage(1);
        }
    };

    const onSubmitUpdate = async (values) => {
        try {
            const result = await updateCurrency(values?.id, values);

            if (!result) {
                throw new Error(translate('currency.error_update'));
            }

            if (result) {
                await getCurrencies({
                    code: codeDebounce,
                    overrideFilter,
                    pager: {
                        page,
                        pageSize: MAX_PAGE,
                    },
                });
            }
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        getCurrencies({
            code: codeDebounce,
            overrideFilter,
            pager: {
                page,
                pageSize: MAX_PAGE,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, codeDebounce, overrideFilter]);

    return (
        <Layout contentClassName={classes.content}>
            <Title value={translate('currency.title')} />

            <Grid2 container justifyContent="flex-start" alignItems="center" flexWrap={{ xs: 'wrap', md: 'nowrap' }} spacing={2} my={4}>
                <Grid2 container sx={{ width: { xs: '100%', sm: '75%', md: '45%' } }}>
                    <TextInput
                        input={{
                            slotProps: {
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                },
                            },
                        }}
                        meta={{ error: null, touched: null }}
                        onChange={onChangeCode}
                        value={code}
                        label={translate('currency.search_label')}
                        placeholder={`${translate('currency.search_label')}...`}
                    />
                </Grid2>
                <Grid2 container sx={{ width: { xs: '35%', md: '15%' } }}>
                    <SelectComponent value={overrideFilter} values={overrideOptions} onChange={onChangeFilter} style={{ width: '100%' }} label={translate('currency.rate_label')} />
                </Grid2>
            </Grid2>

            <div style={{ paddingBottom: '2em' }}>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <Fade in={!loading}>
                        <div>
                            <BasicTable size="small" columns={columns} values={prettifyCurrencies(currencies)} />
                            <br />
                            <Grid2 container justifyContent="center" alignItems="center">
                                <Grid2>{total / MAX_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePageChange} totalItems={total} itemsPerPage={MAX_PAGE} />}</Grid2>
                            </Grid2>
                        </div>
                    </Fade>
                )}
            </div>
        </Layout>
    );
};

export default CurrenciesPage;
